import React, { useEffect, useState } from "react";
import { api } from "../api";
import "./PushNotificationSettings.css";

const TEXT = {
  en: {
    title: "Device notifications",
    intro:
      "Receive important NextScenes updates on this device, even when the site is not open.",
    unsupported: "This browser does not support web notifications.",
    notConfigured:
      "Push delivery is not yet configured on the live server.",
    permissionDenied:
      "Notifications are blocked in this browser’s settings.",
    enabled: "Notifications are enabled on this device.",
    disabled: "Notifications are not enabled on this device.",
    enable: "Enable notifications",
    enabling: "Enabling…",
    disable: "Disable on this device",
    disabling: "Disabling…",
    test: "Send test notification",
    testing: "Sending test…",
    testSent: "A test notification was sent to your enabled device.",
    enabledNotice: "Notifications have been enabled on this device.",
    disabledNotice: "Notifications have been disabled on this device.",
    error: "The notification setting could not be updated.",
  },
  fr: {
    title: "Notifications sur cet appareil",
    intro:
      "Recevez les mises à jour importantes de NextScenes sur cet appareil, même lorsque le site n’est pas ouvert.",
    unsupported:
      "Ce navigateur ne prend pas en charge les notifications Web.",
    notConfigured:
      "L’envoi des notifications n’est pas encore configuré sur le serveur.",
    permissionDenied:
      "Les notifications sont bloquées dans les réglages de ce navigateur.",
    enabled: "Les notifications sont activées sur cet appareil.",
    disabled: "Les notifications ne sont pas activées sur cet appareil.",
    enable: "Activer les notifications",
    enabling: "Activation…",
    disable: "Désactiver sur cet appareil",
    disabling: "Désactivation…",
    test: "Envoyer une notification test",
    testing: "Envoi du test…",
    testSent: "Une notification test a été envoyée à votre appareil.",
    enabledNotice: "Les notifications ont été activées sur cet appareil.",
    disabledNotice: "Les notifications ont été désactivées sur cet appareil.",
    error: "Le réglage des notifications n’a pas pu être mis à jour.",
  },
};

function getText(lang) {
  return TEXT[String(lang || "en").toLowerCase()] || TEXT.en;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

function deviceName() {
  const platform = String(navigator.platform || "").trim();
  const touch = Number(navigator.maxTouchPoints || 0);

  if (/iPhone/i.test(navigator.userAgent)) return "iPhone";
  if (/iPad/i.test(navigator.userAgent)) return "iPad";
  if (/Mac/i.test(platform) && touch > 1) return "iPad";
  if (/Mac/i.test(platform)) return "Mac";
  if (/Android/i.test(navigator.userAgent)) return "Android device";
  if (/Win/i.test(platform)) return "Windows device";

  return "This device";
}

export default function PushNotificationSettings({ lang = "en" }) {
  const t = getText(lang);
  const [supported, setSupported] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [publicKey, setPublicKey] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      const hasSupport =
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window;

      if (!hasSupport) {
        if (alive) setSupported(false);
        return;
      }

      try {
        const [config, registration] = await Promise.all([
          api.get("/api/push/config"),
          navigator.serviceWorker.ready,
        ]);

        if (!alive) return;

        setConfigured(Boolean(config?.configured));
        setPublicKey(String(config?.publicKey || ""));
        setPermission(Notification.permission);

        const subscription =
          await registration.pushManager.getSubscription();

        if (!alive) return;
        setEnabled(Boolean(subscription));
      } catch (err) {
        if (!alive) return;
        setError(err?.message || t.error);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [t.error]);

  async function enableNotifications() {
    setBusy("enable");
    setNotice("");
    setError("");

    try {
      if (!configured || !publicKey) {
        throw new Error(t.notConfigured);
      }

      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== "granted") {
        throw new Error(t.permissionDenied);
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      await api.post("/api/push/subscribe", {
        subscription: subscription.toJSON(),
        deviceName: deviceName(),
      });

      setEnabled(true);
      setNotice(t.enabledNotice);
    } catch (err) {
      setError(err?.message || t.error);
    } finally {
      setBusy("");
    }
  }

  async function disableNotifications() {
    setBusy("disable");
    setNotice("");
    setError("");

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await api.delBody("/api/push/unsubscribe", {
          endpoint: subscription.endpoint,
        });
        await subscription.unsubscribe();
      }

      setEnabled(false);
      setNotice(t.disabledNotice);
    } catch (err) {
      setError(err?.message || t.error);
    } finally {
      setBusy("");
    }
  }

  async function sendTest() {
    setBusy("test");
    setNotice("");
    setError("");

    try {
      await api.post("/api/push/test", {});
      setNotice(t.testSent);
    } catch (err) {
      setError(err?.message || t.error);
    } finally {
      setBusy("");
    }
  }

  if (!supported) {
    return (
      <section className="ns-push-settings" aria-label={t.title}>
        <h3>{t.title}</h3>
        <p>{t.unsupported}</p>
      </section>
    );
  }

  const blocked = permission === "denied";

  return (
    <section className="ns-push-settings" aria-label={t.title}>
      <div className="ns-push-settings__copy">
        <p className="ns-push-settings__kicker">NextScenes PWA</p>
        <h3>{t.title}</h3>
        <p>{t.intro}</p>

        <div
          className={
            enabled
              ? "ns-push-settings__state is-enabled"
              : "ns-push-settings__state"
          }
        >
          <span aria-hidden="true">{enabled ? "✓" : "○"}</span>
          <strong>{enabled ? t.enabled : t.disabled}</strong>
        </div>

        {!configured ? (
          <p className="ns-push-settings__warning">{t.notConfigured}</p>
        ) : null}

        {blocked ? (
          <p className="ns-push-settings__warning">{t.permissionDenied}</p>
        ) : null}

        {notice ? (
          <p className="ns-push-settings__notice" role="status">
            {notice}
          </p>
        ) : null}

        {error ? (
          <p className="ns-push-settings__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="ns-push-settings__actions">
        {!enabled ? (
          <button
            type="button"
            className="ns-button ns-button-primary"
            onClick={enableNotifications}
            disabled={Boolean(busy) || !configured || blocked}
          >
            {busy === "enable" ? t.enabling : t.enable}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="ns-button ns-button-primary"
              onClick={sendTest}
              disabled={Boolean(busy)}
            >
              {busy === "test" ? t.testing : t.test}
            </button>

            <button
              type="button"
              className="ns-button ns-button-outline"
              onClick={disableNotifications}
              disabled={Boolean(busy)}
            >
              {busy === "disable" ? t.disabling : t.disable}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
